/**
 * useKdsSync — 3-layer offline / LAN sync for the KDS (Kitchen Display System)
 *
 * Layer 1: Firestore offline persistence (IndexedDB) — handled in firebase.js
 * Layer 2: BroadcastChannel — instant sync across tabs on the same device (zero-latency)
 * Layer 3: WebSocket LAN relay (server.js /ws) — syncs all devices on the same WiFi
 *           even when Firebase / internet is completely down
 *
 * Usage:
 *   const { lanOnline, netOnline, broadcast } = useKdsSync({ onPatch });
 *
 *   onPatch(orderId, patch) — called whenever another device/tab updates a ticket
 *   broadcast(orderId, patch) — call after any local Firestore write to propagate instantly
 *
 * LAN URL config:
 *   Production: set localStorage key "kds_lan_url" = "ws://192.168.1.X:3001/ws"
 *   Dev:        auto-connects to ws://localhost:3001/ws
 */

import { useEffect, useRef, useState, useCallback } from 'react';

const BC_CHANNEL  = 'kds_ticket_sync';
const WS_URL_KEY  = 'kds_lan_url';

// Stable per-tab device ID (not persisted — just for de-duplication within a session)
const DEVICE_ID = `kds_${Math.random().toString(36).slice(2, 9)}`;

function resolveWsUrl() {
  try {
    const saved = localStorage.getItem(WS_URL_KEY);
    if (saved) return saved;
  } catch {}
  // Auto-connect in dev or when running on the LAN server itself
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return `ws://${host}:3001/ws`;
  }
  return null; // Production: must be configured manually via setKdsLanUrl()
}

export function useKdsSync({ onPatch }) {
  const [lanOnline, setLanOnline] = useState(false);
  const [netOnline, setNetOnline] = useState(() => navigator.onLine);
  const wsRef      = useRef(null);
  const retryTimer = useRef(null);
  const bcRef      = useRef(null);
  const onPatchRef = useRef(onPatch);
  useEffect(() => { onPatchRef.current = onPatch; }, [onPatch]);

  // ── BroadcastChannel (same-device, multi-tab) ─────────────────────────────
  useEffect(() => {
    try {
      const bc = new BroadcastChannel(BC_CHANNEL);
      bcRef.current = bc;
      bc.onmessage = (e) => {
        const { type, orderId, patch, deviceId } = e.data || {};
        if (type === 'ticket_patch' && deviceId !== DEVICE_ID) {
          onPatchRef.current?.(orderId, patch);
        }
      };
      return () => { try { bc.close(); } catch {} };
    } catch {
      return undefined;
    }
  }, []);

  // ── WebSocket (LAN relay via server.js) ───────────────────────────────────
  useEffect(() => {
    let destroyed = false;

    function connect() {
      if (destroyed) return;
      const url = resolveWsUrl();
      if (!url) return;
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!destroyed) setLanOnline(true);
        };
        ws.onclose = () => {
          if (!destroyed) {
            setLanOnline(false);
            retryTimer.current = setTimeout(connect, 4000);
          }
        };
        ws.onerror = () => {
          try { ws.close(); } catch {}
        };
        ws.onmessage = (e) => {
          try {
            const { type, orderId, patch, deviceId } = JSON.parse(e.data);
            if (type === 'ticket_patch' && deviceId !== DEVICE_ID) {
              onPatchRef.current?.(orderId, patch);
            }
          } catch {}
        };
      } catch {}
    }

    connect();

    const onOnline  = () => setNetOnline(true);
    const onOffline = () => setNetOnline(false);
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      destroyed = true;
      clearTimeout(retryTimer.current);
      try { wsRef.current?.close(); } catch {}
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // ── Broadcast a local change to all other devices/tabs ───────────────────
  const broadcast = useCallback((orderId, patch) => {
    const msg = JSON.stringify({
      type: 'ticket_patch',
      orderId,
      patch,
      deviceId: DEVICE_ID,
      ts: Date.now(),
    });

    // WebSocket → other LAN devices
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(msg);
    }

    // BroadcastChannel → other tabs on same device
    try {
      bcRef.current?.postMessage({
        type: 'ticket_patch',
        orderId,
        patch,
        deviceId: DEVICE_ID,
      });
    } catch {}
  }, []);

  return { lanOnline, netOnline, broadcast };
}

/** Save LAN WebSocket URL for production use (call from settings UI) */
export function setKdsLanUrl(url) {
  try { localStorage.setItem(WS_URL_KEY, url.trim()); } catch {}
}

/** Read saved LAN URL */
export function getKdsLanUrl() {
  try { return localStorage.getItem(WS_URL_KEY) || ''; } catch { return ''; }
}
